import { Controller, useFormContext } from "react-hook-form";

import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { SimpleRadioGroup } from "src/base-components/SimpleRadioGroup";
import { ConfigFieldsetCard as FieldsetCard } from "src/connections/config/FieldsetCard";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import { KeyValuePairField } from "src/connections/config/KeyValuePairField";
import {
  AvailableEnvironmentPrefixes,
  ConnectionConfigInputs,
} from "src/connections/types";
import { ErrorHint } from "src/design-system/ErrorHint";

type Props = {
  environmentPrefix: AvailableEnvironmentPrefixes;
};

const HASH_ALGORITHMS: { key: string; value: string }[] = [
  { key: "sha1", value: "SHA-1" },
  { key: "sha256", value: "SHA-256" },
  { key: "sha512", value: "SHA-512" },
];

const SIGNATURE_ENCODINGS: { key: string; value: string }[] = [
  { key: "hex", value: "Hex" },
  { key: "base64", value: "Base64" },
];

export const HmacFieldset: React.FC<Props> = ({ environmentPrefix }) => {
  const {
    formState: { errors: formErrors },
    control,
    register,
    watch,
  } = useFormContext<ConnectionConfigInputs>();

  // This will have to change once we support multiple environments
  const errors =
    environmentPrefix === ""
      ? formErrors?.hmacConfig
      : formErrors.nonProdEnvConfigs?.sandbox?.hmacConfig;

  const isQueryParam =
    watch(`${environmentPrefix}hmacConfig.signature_location`) ===
    "query_param";

  return (
    <>
      <FieldsetCard>
        <FormItem gap="sm">
          <KeyValuePairField
            cta="Add secret"
            dataLoc="add-secret-button"
            name={`${environmentPrefix}secrets`}
            showHeader={false}
            isSensitive
          />
        </FormItem>
      </FieldsetCard>
      <FieldsetCard>
        <FormItem gap="xs" label="Signature location">
          <label className="mb-2 flex font-normal-13px text-gray-700">
            <Controller
              control={control}
              name={`${environmentPrefix}hmacConfig.signature_location`}
              render={(props) => (
                <SimpleRadioGroup
                  className="mr-2"
                  orientation="horizontal"
                  value={props.field.value ?? "header"}
                  onValueChange={props.field.onChange}
                >
                  <SimpleRadioGroup.Item
                    label="Header"
                    labelClassName="pl-1.5 mr-8"
                    value="header"
                  />
                  <SimpleRadioGroup.Item
                    label="Query parameter"
                    labelClassName="pl-1.5"
                    value="query_param"
                  />
                </SimpleRadioGroup>
              )}
            />
          </label>
        </FormItem>
        <div className="mb-4 h-[1px] w-full bg-gray-200"></div>
        {isQueryParam ? (
          <FormItem
            description="The name of the query parameter that carries the request signature"
            gap="sm"
            label="Parameter name"
            isRequired
          >
            {Boolean(errors?.signature_field_name) && (
              <ErrorHint>{errors?.signature_field_name?.message}</ErrorHint>
            )}
            <Input
              errored={isFieldErrored(errors, "signature_field_name")}
              {...register(
                `${environmentPrefix}hmacConfig.signature_field_name`,
                {
                  required: "Parameter name is required",
                },
              )}
              placeholder="e.g. signature"
              fullWidth
            />
          </FormItem>
        ) : (
          <FormItem
            description="The name of the header that carries the request signature"
            gap="sm"
            label="Signature header"
            isRequired
          >
            {Boolean(errors?.signature_field_name) && (
              <ErrorHint>{errors?.signature_field_name?.message}</ErrorHint>
            )}
            <Input
              errored={isFieldErrored(errors, "signature_field_name")}
              {...register(
                `${environmentPrefix}hmacConfig.signature_field_name`,
                {
                  required: "Signature header is required",
                },
              )}
              placeholder="e.g. X-Signature"
              fullWidth
            />
          </FormItem>
        )}
        <FormItem
          description="The hash algorithm used to compute the signature"
          gap="sm"
          label="Hash algorithm"
        >
          <div className="w-full">
            <Controller
              control={control}
              name={`${environmentPrefix}hmacConfig.hash_algorithm`}
              render={(props) => (
                <FixedSimpleDropdown
                  buttonDataLoc={`${environmentPrefix}hmac-hash-algorithm`}
                  elements={HASH_ALGORITHMS}
                  itemsWidth="w-full"
                  placeholder="Select hash algorithm"
                  selectedKey={props.field.value}
                  onSelect={(value) => props.field.onChange(value)}
                />
              )}
            />
          </div>
        </FormItem>
        <FormItem
          description="How the computed signature is encoded before it is sent"
          gap="sm"
          label="Signature encoding"
        >
          <div className="w-full">
            <Controller
              control={control}
              name={`${environmentPrefix}hmacConfig.signature_encoding`}
              render={(props) => (
                <FixedSimpleDropdown
                  buttonDataLoc={`${environmentPrefix}hmac-signature-encoding`}
                  elements={SIGNATURE_ENCODINGS}
                  itemsWidth="w-full"
                  placeholder="Select signature encoding"
                  selectedKey={props.field.value}
                  onSelect={(value) => props.field.onChange(value)}
                />
              )}
            />
          </div>
        </FormItem>
        <FormItem
          description="An optional string prepended to the signature (e.g. sha256=)"
          gap="sm"
          label="Signature prefix"
        >
          <Input
            errored={isFieldErrored(errors, "signature_prefix")}
            {...register(`${environmentPrefix}hmacConfig.signature_prefix`)}
            placeholder="e.g. sha256="
            fullWidth
          />
        </FormItem>
      </FieldsetCard>
    </>
  );
};
